import { useState, useCallback, useEffect, useRef } from 'react'
import { toast } from 'sonner'
import i18n from '@/lib/i18n'
import type { ProjectTask, TaskStatus } from '@/types/conversations'

interface TaskActivity {
  current: string | null
  history: string[]
}

interface UseProjectTasksOptions {
  projectId: number
  initialAutoRun?: boolean
}

export function useProjectTasks({ projectId, initialAutoRun = false }: UseProjectTasksOptions) {
  const [tasks, setTasks] = useState<ProjectTask[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [autoRun, setAutoRun] = useState(initialAutoRun)
  const [taskActivities, setTaskActivities] = useState<Record<number, TaskActivity>>({})
  const projectIdRef = useRef(projectId)
  projectIdRef.current = projectId

  const csrfToken = () =>
    document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''

  const fetchTasks = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await fetch(`/projects/${projectIdRef.current}/tasks`)
      if (response.ok) {
        const data = await response.json()
        setTasks(data)
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const startTask = useCallback(async (taskId: number) => {
    const response = await fetch(`/projects/${projectIdRef.current}/tasks/${taskId}/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
    })
    if (response.ok) {
      const data = await response.json()
      setTasks(prev => prev.map(t => t.id === taskId ? data : t))
      return data
    }
    const error = await response.json().catch(() => null)
    return { error: error?.error || 'Failed to start task' } // Backend errors are English; frontend fallback kept simple
  }, [])

  const markDone = useCallback(async (taskId: number) => {
    const response = await fetch(`/projects/${projectIdRef.current}/tasks/${taskId}/mark_done`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
    })
    if (response.ok) {
      setTasks(prev => prev.filter(t => t.id !== taskId))
      return true
    }
    if (response.status === 409) {
      toast.error(i18n.t('conversations:taskBoard.stillRunning'))
    }
    return false
  }, [])

  const toggleAutoRun = useCallback(async () => {
    const response = await fetch(`/projects/${projectIdRef.current}/toggle_auto_run`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
    })
    if (response.ok) {
      const data = await response.json()
      setAutoRun(data.auto_run_tasks)
      return data.auto_run_tasks
    }
    return null
  }, [])

  const deleteTask = useCallback(async (taskId: number) => {
    const response = await fetch(`/projects/${projectIdRef.current}/tasks/${taskId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
    })
    if (response.ok) {
      setTasks(prev => prev.filter(t => t.id !== taskId))
      return true
    }
    return false
  }, [])

  const stopTask = useCallback(async (taskId: number) => {
    const response = await fetch(`/projects/${projectIdRef.current}/tasks/${taskId}/stop`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
    })
    if (response.ok) {
      const data = await response.json()
      setTasks(prev => prev.map(t => t.id === taskId ? data : t))
      return data
    }
    return null
  }, [])

  const acceptTask = useCallback(async (taskId: number) => {
    const response = await fetch(`/projects/${projectIdRef.current}/tasks/${taskId}/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
    })
    if (response.ok) {
      setTasks(prev => prev.filter(t => t.id !== taskId))
      return true
    }
    const error = await response.json().catch(() => null)
    return { error: error?.error || 'Failed to accept task' }
  }, [])

  const discardTask = useCallback(async (taskId: number) => {
    const response = await fetch(`/projects/${projectIdRef.current}/tasks/${taskId}/discard`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
    })
    if (response.ok) {
      setTasks(prev => prev.filter(t => t.id !== taskId))
      return true
    }
    const error = await response.json().catch(() => null)
    return { error: error?.error || 'Failed to discard task' }
  }, [])

  const handleTaskEvent = useCallback((event: { type: string; data: any }) => {
    if (event.type === 'task_created') {
      const newTask = event.data as ProjectTask
      setTasks(prev => {
        if (prev.find(t => t.id === newTask.id)) return prev
        return [...prev, newTask]
      })
    }
    if (event.type === 'task_updated') {
      const { task_id, status, target, title, description, todos, coding_worker_id, coding_worker_name, merge_commit_sha, recording_pending, recording_url } = event.data
      setTasks(prev => {
        const existing = prev.find(t => t.id === task_id)
        if (existing) {
          if (status === 'done') {
            return prev.filter(t => t.id !== task_id)
          }
          // If task went back to in_progress (request_changes), refetch to get updated change_requests
          if (status === 'in_progress' && existing.status === 'review') {
            fetchTasks()
            return prev
          }
          const updates: Partial<ProjectTask> = {}
          if (status) updates.status = status as TaskStatus
          if (target) updates.target = target as 'worker' | 'main'
          if (title) updates.title = title
          if (description !== undefined) updates.description = description
          if (todos) updates.todos = todos
          if (coding_worker_id !== undefined) updates.coding_worker_id = coding_worker_id
          if (coding_worker_name !== undefined) updates.coding_worker_name = coding_worker_name
          if (merge_commit_sha !== undefined) updates.merge_commit_sha = merge_commit_sha
          if (recording_pending !== undefined) updates.recording_pending = recording_pending
          if (recording_url !== undefined) updates.recording_url = recording_url
          return prev.map(t => t.id === task_id ? { ...t, ...updates } : t)
        }
        // Task not in local state — could be a race with task_created (normal)
        // or a missed event after WebSocket reconnect. Check after a delay:
        // only refetch if the task is still missing (avoids flicker when
        // task_created arrives moments later and adds it).
        const missingTaskId = task_id
        setTimeout(() => {
          setTasks(current => {
            if (!current.find(t => t.id === missingTaskId)) {
              fetchTasks()
            }
            return current
          })
        }, 500)
        return prev
      })
      // Clear activity when task finishes
      if (['review', 'done', 'backlog'].includes(status)) {
        setTaskActivities(prev => {
          const next = { ...prev }
          delete next[task_id]
          return next
        })
      }
      // Toast when task reverts to backlog from dispatch failure (not user-initiated stop)
      if (status === 'backlog' && event.data.failure_reason && !event.data.stopped_by_user) {
        const taskTitle = tasks.find(t => t.id === task_id)?.title || `Task #${task_id}`
        const reason = event.data.failure_reason === 'agent_busy' ? 'agent was busy' : 'build failed'
        toast.error(`"${taskTitle}" moved back to queue — ${reason}`)
      }
    }
    if (event.type === 'task_activity') {
      const { task_id, activity } = event.data
      setTaskActivities(prev => {
        const existing = prev[task_id] || { current: null, history: [] }
        return {
          ...prev,
          [task_id]: {
            current: activity,
            history: existing.current ? [...existing.history.slice(-4), existing.current] : existing.history
          }
        }
      })
    }
  }, [fetchTasks])

  useEffect(() => {
    fetchTasks()
  }, [fetchTasks])

  const buildingTask = tasks.find(t => t.status === 'in_progress')
  const buildingTasks = tasks.filter(t => t.status === 'in_progress')

  return {
    tasks,
    buildingTask,
    buildingTasks,
    taskActivities,
    autoRun,
    isLoading,
    fetchTasks,
    startTask,
    markDone,
    stopTask,
    acceptTask,
    discardTask,
    deleteTask,
    toggleAutoRun,
    handleTaskEvent,
  }
}
